import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { get, head, last } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { CmsEntities, bindToContent } from 'client/data/models/cms';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { getMostSoldTypeContentPath } from 'site-modules/shared/utils/upper-funnel/most-sold-type-infographic';
import { OdometerWrapperProgressive } from 'site-modules/shared/components/odometer-wrapper/odometer-wrapper-progressive';
import { ShareThisChart } from 'site-modules/shared/components/share-this-chart/share-this-chart';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { PoweredByEdmunds } from 'site-modules/shared/components/powered-by-edmunds/powered-by-edmunds';
import { getCurrentDate } from 'client/site-modules/shared/utils/date-utils';
import { getPlural } from 'client/utils/plural';
import { getUserFriendlyTypeName } from 'site-modules/shared/utils/upper-funnel/type-mapping';

import { Player } from './player';
import { YearsSlider } from './years-slider';
import { NationalStats } from './national-stats';

/* LAZY IMPORTS */
import lazyImports from './lazy-imports';

import './most-sold-type-infographic.scss';

const { USMap } = lazyImports;

const ANIMATION_SPEED = 2000;

function getStatesColorMapping({ stateVehicleSales, colorsMapping }) {
  const statesColorMapping = {};

  stateVehicleSales.forEach(state => {
    const { bestSellerName, stateCode } = state;
    const color = colorsMapping[bestSellerName];

    if (statesColorMapping[color]) {
      statesColorMapping[color].push(stateCode);
    } else {
      statesColorMapping[color] = [stateCode];
    }
  });

  return statesColorMapping;
}

function getStatesLabelsMapping(stateVehicleSales) {
  const statesLabelMapping = {};

  stateVehicleSales.forEach(state => {
    const { bestSellerName, stateCode } = state;
    statesLabelMapping[stateCode] = bestSellerName;
  });

  return statesLabelMapping;
}

export function makePageUrl(type) {
  if (type.slice(-4, type.length) === 'suvs') {
    const typeName = 'suv';
    const subtypeName = type.slice(0, type.length - 5);
    const pageUrl = `https://www.edmunds.com/${typeName}/${subtypeName}/#best-selling-${subtypeName}-${typeName}s`;
    return pageUrl;
  }
  return `https://www.edmunds.com/${type}/#best-selling-${type}s`;
}

export function makeTablePageUrl(type) {
  if (type.slice(-4, type.length) === 'suvs') {
    const typeName = 'suv';
    const subtypeName = type.slice(0, type.length - 5);
    const tablePageUrl = `https://www.edmunds.com/widgets/most-sold-type/table/?type=${subtypeName}-${typeName}`;
    return tablePageUrl;
  }
  return `https://www.edmunds.com/widgets/most-sold-type/table/?type=${type}`;
}

export class MostSoldTypeInfographicUI extends Component {
  static propTypes = {
    firstYear: PropTypes.number.isRequired,
    lastYear: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
    isMobile: PropTypes.bool,
    nationalStats: PropTypes.objectOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          makeName: PropTypes.string,
          nationalCount: PropTypes.number,
          rank: PropTypes.number,
          url: PropTypes.string,
        })
      )
    ),
    colorsMapping: PropTypes.objectOf(PropTypes.string),
    stateVehicleSales: PropTypes.objectOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          bestSellerCount: PropTypes.number,
          bestSellerName: PropTypes.string,
          bestSellerUrl: PropTypes.string,
          stateCode: PropTypes.string,
        })
      )
    ),
    title: PropTypes.string,
    sectionContent: PropTypes.arrayOf(PropTypes.string),
    disclaimer: PropTypes.string,
  };

  static defaultProps = {
    content: DEFAULT_CONTENT,
    isMobile: false,
    nationalStats: {},
    colorsMapping: {},
    stateVehicleSales: {},
    title: '',
    sectionContent: [],
    disclaimer: null,
  };

  state = {
    isAnimationInProgress: false,
    isInitialAnimationStarted: false,
    currentYear: this.props.firstYear,
  };

  onSliderChange = value => {
    this.stopAnimation();
    this.setState({ currentYear: +value });
  };

  onNextClick = () => {
    this.stopAnimation();
    this.goToNextYear();
  };

  onPrevClick = () => {
    this.stopAnimation();
    this.goToPrevYear();
  };

  ref = React.createRef();
  observer;

  animationIntervalId;

  startAnimation = () => {
    this.setState({ isAnimationInProgress: true, isInitialAnimationStarted: true });

    this.animationIntervalId = setInterval(() => {
      this.goToNextYear();
    }, ANIMATION_SPEED);
  };

  stopAnimation = () => {
    clearInterval(this.animationIntervalId);
    this.setState({ isAnimationInProgress: false });
  };

  toggleAnimation = () => {
    const { isAnimationInProgress } = this.state;

    if (isAnimationInProgress) {
      this.stopAnimation();
    } else {
      this.startAnimation();
    }
  };

  goToNextYear = () => {
    const { lastYear, firstYear } = this.props;
    const { currentYear } = this.state;

    this.setState({ currentYear: currentYear < lastYear ? currentYear + 1 : firstYear });
  };

  goToPrevYear = () => {
    const { lastYear, firstYear } = this.props;
    const { currentYear } = this.state;

    this.setState({ currentYear: currentYear > firstYear ? currentYear - 1 : lastYear });
  };

  render() {
    const {
      firstYear,
      lastYear,
      type,
      isMobile,
      nationalStats,
      colorsMapping,
      stateVehicleSales,
      title,
      sectionContent,
      disclaimer,
    } = this.props;
    const { currentYear, isAnimationInProgress } = this.state;

    const currentYearNationalStats = get(nationalStats, currentYear, []);
    const currentYearStateVehicleSales = get(stateVehicleSales, currentYear, []);

    const statesColorMapping = getStatesColorMapping({
      stateVehicleSales: currentYearStateVehicleSales,
      colorsMapping,
    });

    const statesLabelMapping = getStatesLabelsMapping(currentYearStateVehicleSales);

    const shareableWidgetData = {
      widgetName: 'most-sold-type',
      defaultWidth: 400,
      defaultHeight: 685,
      minWidth: 300,
      minHeight: 600,
      defaultTitle: title,
      queryParams: { type },
    };

    const playerAndSlider = (
      <div className="mb-1 mb-lg-0">
        <Player
          isAnimationInProgress={isAnimationInProgress}
          onPlayPauseClick={this.toggleAnimation}
          onNextClick={this.onNextClick}
          onPrevClick={this.onPrevClick}
        />
        <YearsSlider
          firstYear={firstYear}
          lastYear={lastYear}
          currentYear={currentYear}
          onChange={this.onSliderChange}
        />
      </div>
    );

    return (
      <Fragment>
        <div className="most-sold-type-infographic py-1 px-1 p-lg-2" ref={this.ref} aria-live="polite">
          <Row className="widget-header d-flex justify-content-between align-items-center mb-0_5 mt-0_5">
            <Col xs={12} md={8}>
              <h2
                className="heading-wrapper heading-3 d-flex align-items-center mb-lg-0 flex-wrap"
                aria-label={`${title} from ${firstYear} to ${lastYear}`}
                aria-live="polite"
              >
                <span className="me-0_5 d-inline-block">{title} of</span>
                <OdometerWrapperProgressive value={currentYear} duration={1} format="d" />
              </h2>
            </Col>
            <Col xs={12} md={4}>
              <ShareThisChart
                pageTitle={title}
                pageUrl={makePageUrl(type)}
                className="text-primary-darker ms-md-auto"
                shareableWidgetData={shareableWidgetData}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={8}>
              <p className="size-14">
                <a href={makeTablePageUrl(type)} className="text-primary-darker" rel="nofollow">
                  Table view of{' '}
                  <span className="visually-hidden">
                    {title} in the US from {firstYear} to {lastYear}
                  </span>{' '}
                  <span aria-hidden="true">data</span>
                </a>
              </p>
              <p className="size-14 mb-2">
                Vehicles included in the data set are exclusively retail registrations to individuals and do not include
                rental sales or registrations from government bodies*
              </p>
            </Col>
          </Row>
          <Row>
            <Col tag="figure" xs={12} md={9}>
              <div
                className="map-wrapper mw-100 mb-1_5"
                aria-live="off"
                role="img"
                aria-label={`Map of ${title} in the US from ${firstYear} to ${lastYear}. See linked table view of data for more details.`}
              >
                <AspectRatioContainer aspectRatio="11:7" aria-hidden="true">
                  <div className="pe-1 pe-lg-2" aria-live="off">
                    <USMap
                      statesColorMap={statesColorMapping}
                      statesLabelMap={statesLabelMapping}
                      hasDynamicLabels
                      isMobile={isMobile}
                    />
                  </div>
                </AspectRatioContainer>
              </div>
              <figcaption className="visually-hidden">
                <a href={makeTablePageUrl(type)} className="text-primary-darker" rel="nofollow" tabIndex={-1}>
                  Table view of {title} in the US from {firstYear} to {lastYear}
                </a>
              </figcaption>
              {isMobile && playerAndSlider}
            </Col>
            <Col xs={12} md={3} className="ps-lg-1 mb-1_5" style={{ minHeight: '190px' }}>
              <NationalStats nationalStats={currentYearNationalStats} colorsMapping={colorsMapping} />
            </Col>
          </Row>
          {!isMobile && playerAndSlider}
          {!!sectionContent.length && (
            <Fragment>
              <h3 className="size-24 fw-bold text-primary-darker mt-1 mb-1">Some Takeaways</h3>
              <ul className="mb-1_5">
                {sectionContent.map(item => (
                  <li key={item.substr(0, 16)} className="size-16 text-gray-darker mb-0_5 mb-lg-0_25">
                    {item}
                  </li>
                ))}
              </ul>
            </Fragment>
          )}
          <PoweredByEdmunds className="mb-1" />
          <p className="disclaimer mb-0">{disclaimer}</p>
        </div>
        <hr className="mt-1_5 mb-2" />
      </Fragment>
    );
  }
}

function getChild(showMap, child) {
  return showMap ? child : 'skip';
}

export function MostSoldTypeInfographicWrapper({ content = DEFAULT_CONTENT, type, isMobile = false, showMap = true }) {
  const mainEntry = content.child('main');

  const years = mainEntry
    .child(getChild(showMap, 'national-stats'))
    .children()
    .map(entry => +entry.metadata('rank-title').value(''))
    .sort();
  const prevYear = getCurrentDate().getFullYear() - 1;
  const firstYear = head(years) || prevYear;
  const lastYear = last(years) || prevYear;

  const title = content.title || `Top Selling ${getPlural(getUserFriendlyTypeName(type), undefined, undefined, true)}`;
  const nationalStats = {};

  mainEntry
    .child(getChild(showMap, 'national-stats'))
    .children()
    .forEach(entry => {
      const year = +entry.metadata('rank-title').value('');
      const yearStats = entry.children().map(childStatsEntry => ({
        rank: +childStatsEntry.metadata('rank').value(''),
        url: childStatsEntry.metadata('url').value(''),
        makeName: childStatsEntry.metadata('make-name').value(''),
        nationalCount: +childStatsEntry.metadata('national-count').value(''),
      }));

      nationalStats[year] = yearStats;
    });

  const colorsMapping = {};

  mainEntry
    .child(getChild(showMap, 'colors-mapping'))
    .children()
    .forEach(entry => {
      const { color = '', 'vehicle-name': vehicleName = '' } = entry.getAllMetadata();
      colorsMapping[vehicleName] = color;
    });

  const stateVehicleSales = {};

  mainEntry
    .child(getChild(showMap, 'state-vehicle-sales'))
    .children()
    .forEach(yearStateVehicleSalesEntry => {
      const year = get(yearStateVehicleSalesEntry, 'id', '').replace(/\D/g, '');
      const statesSales = yearStateVehicleSalesEntry.children().map(stateSalesEntry => ({
        bestSellerUrl: stateSalesEntry.metadata('best-seller-url').value(''),
        bestSellerName: stateSalesEntry.metadata('best-seller-name').value(''),
        bestSellerCount: +stateSalesEntry.metadata('best-seller-count').value(''),
        stateCode: stateSalesEntry.metadata('state-code').value(''),
      }));

      stateVehicleSales[year] = statesSales;
    });

  const sectionContent = mainEntry.child('section-content').metadata('content').json([]);
  const disclaimer = mainEntry.child('disclaimer').metadata('content').value();

  return (
    <MostSoldTypeInfographicUI
      nationalStats={nationalStats}
      colorsMapping={colorsMapping}
      stateVehicleSales={stateVehicleSales}
      firstYear={firstYear}
      lastYear={lastYear}
      type={type}
      title={title}
      isMobile={isMobile}
      sectionContent={sectionContent}
      disclaimer={disclaimer}
    />
  );
}

MostSoldTypeInfographicWrapper.propTypes = {
  content: CmsEntities.Content,
  type: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  showMap: PropTypes.bool,
};

export const stateToPropsConfig = {
  content: bindToContent(({ type }) => getMostSoldTypeContentPath(type)),
};

export const MostSoldTypeInfographic = connectToModel(MostSoldTypeInfographicWrapper, stateToPropsConfig);
